import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  CheckCircleIcon,
  CalendarDaysIcon,
  ClockIcon,
  BuildingStorefrontIcon,
  CreditCardIcon,
  ClipboardDocumentIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { supabase, database, getAdminStatusCached, notifyPendingBooking } from './lib/supabase';
import TimeSlotGrid, { formatTime } from './TimeSlotGrid';
import Toast, { useToast } from './Toast';
import Header from './Header';
import Footer from './Footer';
import AuthModal from './AuthModal';
import TermsModal from './TermsModal';
import WhatsAppButton from './WhatsAppButton';

function GreenBookingApp() {
  const [, setCourts] = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [availability, setAvailability] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [, setUserProfile] = useState(null);
  const [hasMembership, setHasMembership] = useState(false);
  const [currentStep, setCurrentStep] = useState('booking');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showTermsReadOnly, setShowTermsReadOnly] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [waitingListCounts, setWaitingListCounts] = useState({});
  const { toast, setToast, showToast } = useToast();

  const handleBackToHome = () => {
    setSelectedSlots([]);
    setCustomerInfo({ name: '', phone: '', email: '' });
    setCurrentStep('booking');
    setTermsAccepted(false);
    window.location.href = '/';
  };

  useEffect(() => {
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        await fetchUserProfile(session.user.email);
      }
    };
    init();
    fetchCourts();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'TOKEN_REFRESHED') return;

      if (session?.user) {
        setUser(session.user);
        await fetchUserProfile(session.user.email);
      } else {
        setUser(null);
        setUserProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserProfile = async (email) => {
    const { data, error } = await getAdminStatusCached(email);
    if (!error && data?.[0]) {
      setUserProfile(data[0]);
      setCustomerInfo(prev => ({
        ...prev,
        name: data[0].full_name || prev.name,
        email: data[0].email || prev.email,
        phone: data[0].phone || prev.phone
      }));
      
      const { data: membershipData } = await supabase
        .rpc('has_active_membership', { user_uuid: data[0].id });
      setHasMembership(membershipData || false);
    }
  };

  const fetchCourts = async () => {
    const { data, error } = await supabase
      .from('courts')
      .select('*');

    if (!error) {
      setCourts(data);
      if (data.length > 0) {
        setSelectedCourt(data[0]);
      }
    }
  };

  const fetchWaitingListCountsForSlots = useCallback(async (slots) => {
    if (!selectedCourt || !slots.length) return;

    try {
      const results = await Promise.all(
        slots.map(slot =>
          supabase
            .from('bookings')
            .select('*', { count: 'exact', head: true })
            .eq('court_id', selectedCourt.id)
            .eq('booking_date', selectedDate)
            .eq('slot_time', slot.slot_time)
            .eq('status', 'pending')
            .then(({ count, error }) => ({
              slot_time: slot.slot_time,
              count: error ? 0 : (count || 0)
            }))
        )
      );

      const counts = {};
      results.forEach(r => { counts[r.slot_time] = r.count; });
      setWaitingListCounts(counts);
    } catch (error) {
      console.error('Error fetching waiting list counts:', error);
    }
  }, [selectedCourt, selectedDate]);

  const fetchAvailability = useCallback(async () => {
    if (!selectedCourt) return;

    setLoading(true);
    const { data, error } = await database.getAvailability(selectedDate, selectedCourt.id);

    if (error) {
      console.error('Availability fetch error:', error);
      showToast('Failed to load slots: ' + error.message, 'error');
    } else {
      setAvailability(data || []);
      if (data && data.length > 0) {
        await fetchWaitingListCountsForSlots(data);
      }
    }
    setLoading(false);
  }, [selectedCourt, selectedDate, showToast, fetchWaitingListCountsForSlots]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  useEffect(() => {
    setWaitingListCounts({});
    setSelectedSlots([]);
  }, [selectedCourt, selectedDate]);

  const handleSlotToggle = (slot) => {
    if (slot.slot_type_label !== 'available') return;
    setSelectedSlots(prev => {
      const isSelected = prev.some(s => s.slot_time === slot.slot_time);
      if (isSelected) {
        return prev.filter(s => s.slot_time !== slot.slot_time);
      } else if (slot.is_available) {
        return [...prev, slot];
      }
      return prev;
    });
  };

  const totalAmount = useMemo(() => {
    return selectedSlots.length * (selectedCourt?.hourly_rate || 300);
  }, [selectedSlots.length, selectedCourt?.hourly_rate]);

  const handleBooking = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (selectedSlots.length === 0) {
      showToast('Please select at least one time slot', 'error');
      return;
    }

    if (!customerInfo.name || !customerInfo.phone) {
      showToast('Your profile is missing name or phone. Please update your profile.', 'error');
      return;
    }

    setShowTermsModal(true);
  };

  const handleBookingConfirm = async () => {
    if (!termsAccepted) {
      showToast('Please accept the terms and conditions to proceed', 'error');
      return;
    }

    setShowTermsModal(false);

    if (!customerInfo.name || !customerInfo.phone) {
      showToast('Your profile is missing name or phone. Please update your profile.', 'error');
      return;
    }

    setLoading(true);

    try {
      if (user?.id) {
        const selectedSlotTimes = selectedSlots.map(slot => slot.slot_time);
        
        for (const slotTime of selectedSlotTimes) {
          const { data: existingBookings, error: checkError } = await supabase
            .from('bookings')
            .select('id, status')
            .eq('user_id', user.id)
            .eq('court_id', selectedCourt.id)
            .eq('booking_date', selectedDate)
            .eq('slot_time', slotTime)
            .in('status', ['pending', 'approved']);
          
          if (checkError) {
            console.error('Error checking existing bookings:', checkError);
          } else if (existingBookings && existingBookings.length > 0) {
            showToast(`You already have a booking for ${formatTime(slotTime)} on this date. Please wait for approval or choose a different slot.`, 'error');
            setLoading(false);
            return;
          }
        }
      }

      const { data: currentAvailability } = await database.getAvailability(selectedDate, selectedCourt.id);

      const selectedSlotTimes = selectedSlots.map(slot => slot.slot_time);
      const unavailableSlots = selectedSlotTimes.filter(slotTime => {
        const slot = currentAvailability?.find(s => s.slot_time === slotTime);
        return !slot || !slot.is_available;
      });

      if (unavailableSlots.length > 0) {
        showToast(`Some selected slots are no longer available. Please refresh and select again.`, 'error');
        await fetchAvailability();
        setLoading(false);
        return;
      }

      const slotTimes = selectedSlots.map(slot => slot.slot_time);
      
      const { data, error } = await supabase
        .rpc('create_booking_atomic', {
          p_court_id: selectedCourt.id,
          p_booking_date: selectedDate,
          p_customer_name: customerInfo.name,
          p_customer_phone: customerInfo.phone,
          p_customer_email: customerInfo.email,
          p_selected_slots: slotTimes,
          p_total_amount: totalAmount,
          p_user_id: user?.id || null
        });

      if (error) throw error;

      const result = data[0];

      if (!result.success) {
        showToast(result.message, 'error');
        await fetchAvailability();
        return;
      }

      if (result.failed_slots && result.failed_slots.length > 0) {
        showToast(result.message, 'error');
        await fetchAvailability();
      }

      // Notify admin by email (new pending booking) – fire-and-forget
      notifyPendingBooking({
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        customer_email: customerInfo.email ?? '',
        booking_date: selectedDate,
        time_slots: slotTimes.join(', '),
        total_amount: totalAmount,
      });

      setCurrentStep('confirmation');
      window.scrollTo(0, 0);

    } catch (error) {
      console.error('Booking error:', error);
      showToast('Booking failed: ' + error.message, 'error');
      await fetchAvailability();
    }

    setLoading(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const steps = [
    { id: 1, label: 'Select Date', icon: CalendarDaysIcon, done: !!selectedDate },
    { id: 2, label: 'Pick Slots', icon: ClockIcon, done: selectedSlots.length > 0 },
  ];

  const getQuickDates = () => {
    const dates = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    for (let i = 0; i < 4; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      dates.push({
        value: d.toISOString().split('T')[0],
        day: d.getDate(),
        dayName: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : dayNames[d.getDay()],
        month: monthNames[d.getMonth()],
      });
    }
    return dates;
  };

  const quickDates = getQuickDates();

  // ─── Confirmation Screen ───
  if (currentStep === 'confirmation') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />

        <section className="relative bg-gradient-to-br from-green-600 via-green-700 to-green-800 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/20" />
            <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-white/10" />
          </div>
          <div className="relative container mx-auto px-4 py-10 text-center">
            <div className="bg-white/20 backdrop-blur-sm w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 animate-soft-bounce">
              <CheckCircleIcon className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 animate-smooth-rise">Booking Submitted!</h1>
            <p className="text-green-100 text-lg max-w-xl mx-auto animate-smooth-rise animate-delay-200">
              Your request for {selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''} on {formatDate(selectedDate)} is awaiting admin approval.
            </p>
          </div>
        </section>

        <main className="container mx-auto px-4 py-8 max-w-2xl -mt-4">
          {/* Status Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-6 animate-smooth-rise animate-delay-200">
            <div className="flex items-center gap-3 px-6 py-4 bg-amber-50 border-b border-amber-100">
              <ClockIcon className="h-5 w-5 text-amber-600" />
              <span className="font-semibold text-amber-800">Status: Pending Approval</span>
            </div>
            <div className="px-6 py-5">
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Total Amount</span>
                <span className="text-3xl font-bold text-gray-900">₹{totalAmount.toLocaleString()}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">You'll be notified once your booking is approved.</p>
            </div>
          </div>

          {/* Payment Warning */}
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-6 animate-smooth-rise animate-delay-300">
            <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2">
              <ShieldCheckIcon className="h-5 w-5" />
              Payment Instructions
            </h3>
            <ol className="text-sm text-red-700 space-y-2">
              <li className="flex gap-2"><span className="font-bold text-red-800 flex-shrink-0">1.</span> Complete payment using the methods below</li>
              <li className="flex gap-2"><span className="font-bold text-red-800 flex-shrink-0">2.</span> Take a screenshot of your payment confirmation</li>
              <li className="flex gap-2"><span className="font-bold text-red-800 flex-shrink-0">3.</span> Notify us on WhatsApp (8080517517) immediately. We must confirm your payment to approve your reservation. Failure to notify will result in cancellation without a refund</li>
              <li className="flex gap-2"><span className="font-bold text-red-800 flex-shrink-0">4.</span> Send payment proof and booking details</li>
              <li className="flex gap-2"><span className="font-bold text-red-800 flex-shrink-0">5.</span> Wait for approval and check your My Bookings page</li>
            </ol>
          </div>

          {/* Refund & Payment — Two columns */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6 animate-smooth-rise animate-delay-400">
            {/* Refund Policy */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <div className="bg-amber-100 p-1.5 rounded-lg">
                  <ShieldCheckIcon className="h-4 w-4 text-amber-600" />
                </div>
                <h3 className="font-bold text-amber-800 text-sm">Refund Policy</h3>
              </div>
              <p className="text-sm text-amber-700 leading-relaxed">
                Refunds are applicable only if the slot is cancelled <span className="font-bold">8 hours or more</span> before the scheduled time.
              </p>
              <p className="text-sm text-amber-700 leading-relaxed mt-2">
                No refunds will be provided for late cancellations or no-shows.
              </p>

              {/* Court Timing */}
              <div className="mt-4 pt-3 border-t border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <div className="bg-amber-100 p-1.5 rounded-lg">
                    <ClockIcon className="h-4 w-4 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-amber-800 text-sm">Court Timing</h3>
                </div>
                <p className="text-sm text-amber-700 leading-relaxed">
                  Players are kindly requested to vacate the court promptly at the end of their booked slot. Play beyond the reserved time is <span className="font-bold">strictly not permitted</span>.
                </p>
              </div>
            </div>

            {/* Complete Your Payment */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                  <CreditCardIcon className="h-4 w-4 text-green-600" />
                  Complete Your Payment
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {/* QR Code */}
                <div className="bg-gray-50 rounded-xl p-3 flex flex-col items-center border border-gray-100">
                  <a href={`upi://pay?pa=bestbadmintonclub@cnrb&pn=Best%20Badminton%20Club&am=${totalAmount}&cu=INR`}>
                    <img
                      src="/images/gallery/qr_code.png"
                      alt="UPI QR Code"
                      className="w-32 h-32 object-contain cursor-pointer hover:scale-105 transition-transform"
                    />
                  </a>
                  <p className="text-[10px] text-gray-400 text-center mt-1">Scan or tap to pay</p>
                </div>

                {/* UPI ID */}
                <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] text-gray-400">UPI ID</div>
                      <div className="font-mono font-semibold text-gray-900 text-xs">bestbadmintonclub@cnrb</div>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('bestbadmintonclub@cnrb');
                        showToast('UPI ID copied!', 'success');
                      }}
                      className="flex items-center gap-1 text-[10px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded-md transition-colors"
                    >
                      <ClipboardDocumentIcon className="h-3 w-3" />
                      Copy
                    </button>
                  </div>
                </div>

                {/* GPay Button */}
                <button
                  onClick={() => {
                    window.location.href = `tez://upi/pay?pa=bestbadmintonclub@cnrb&pn=Best%20Badminton%20Club&am=${totalAmount}&cu=INR`;
                  }}
                  className="w-full bg-green-600 text-white py-2 rounded-lg text-xs font-semibold hover:bg-green-700 transition-colors"
                >
                  Open GPay / PhonePe
                </button>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 animate-smooth-rise animate-delay-500">
            <button
              onClick={handleBackToHome}
              className="w-full bg-white text-gray-700 py-3.5 rounded-xl font-semibold border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              Back to Home
            </button>
            <p className="text-sm text-gray-400 text-center pt-1">
              Don't forget to notify admin via WhatsApp (8080517517)
            </p>
          </div>
        </main>

        <WhatsAppButton />
        <Toast toast={toast} onClose={() => setToast(null)} />
      </div>
    );
  }

  // ─── Main Booking View ───
  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <Header />

      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-green-600 via-green-700 to-green-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/20" />
          <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-white/10" />
        </div>
        <div className="relative container mx-auto px-4 pt-6 pb-8 sm:pt-8 sm:pb-10">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1 animate-smooth-rise text-center">Book Your Court</h1>
          <p className="text-green-200 text-sm sm:text-lg animate-smooth-rise animate-delay-100 text-center">
            Reserve your preferred time slots and start playing
          </p>
        </div>
      </section>

      {/* Step Indicator */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-8 max-w-md mx-auto py-3">
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <React.Fragment key={step.id}>
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                      step.done
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      {step.done
                        ? <CheckCircleSolidIcon className="h-4 w-4" />
                        : <Icon className="h-3.5 w-3.5" />
                      }
                    </div>
                    <span className={`text-sm font-medium ${
                      step.done ? 'text-green-700' : 'text-gray-400'
                    }`}>
                      {step.label}
                    </span>
                  </div>
                  {i < steps.length - 1 && (
                    <div className={`w-16 h-0.5 rounded-full transition-colors ${
                      steps[i + 1].done ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 md:py-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">

            {/* Left Column - Date & Slots */}
            <div className="lg:col-span-3 space-y-6">

              {/* Date Selection */}
              <div className="animate-smooth-rise">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <CalendarDaysIcon className="h-5 w-5 text-green-600" />
                  When do you want to play?
                </h2>

                {/* Quick Date Picker + Calendar */}
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-stretch">
                  {quickDates.map((d) => {
                    const isActive = selectedDate === d.value;
                    return (
                      <button
                        key={d.value}
                        onClick={() => setSelectedDate(d.value)}
                        className={`py-2.5 sm:py-3 rounded-xl text-center transition-all border-2 ${
                          isActive
                            ? 'bg-green-600 border-green-600 text-white shadow-md shadow-green-600/20'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-green-300 hover:bg-green-50/50'
                        }`}
                      >
                        <div className={`text-[10px] font-semibold uppercase tracking-wide ${isActive ? 'text-green-100' : 'text-gray-400'}`}>
                          {d.dayName}
                        </div>
                        <div className={`text-lg sm:text-xl font-bold leading-tight ${isActive ? 'text-white' : 'text-gray-900'}`}>
                          {d.day}
                        </div>
                        <div className={`text-[10px] font-medium ${isActive ? 'text-green-100' : 'text-gray-400'}`}>
                          {d.month}
                        </div>
                      </button>
                    );
                  })}

                  {/* Date Picker - inline in grid */}
                  {(() => {
                    const isCalendarDate = !quickDates.some(d => d.value === selectedDate);
                    return (
                      <div className={`col-span-2 sm:col-span-2 rounded-xl border-2 transition-all px-3 pt-3 pb-2 ${
                        isCalendarDate
                          ? 'bg-green-50 border-green-300'
                          : 'bg-white border-gray-200 hover:border-green-300'
                      }`}>
                        <div className={`text-[10px] font-semibold uppercase tracking-wide ${isCalendarDate ? 'text-green-600' : 'text-gray-400'}`}>
                          More Dates
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          <CalendarDaysIcon className={`h-4 w-4 flex-shrink-0 ${isCalendarDate ? 'text-green-600' : 'text-gray-400'}`} />
                          <input
                            type="date"
                            min={new Date().toISOString().split('T')[0]}
                            value={selectedDate}
                            onChange={(e) => {
                              if (e.target.value) setSelectedDate(e.target.value);
                            }}
                            className="w-full bg-transparent text-sm font-semibold text-gray-800 focus:outline-none cursor-pointer"
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Selected date display */}
                <div className="mt-3 bg-green-50 border border-green-100 rounded-lg px-4 py-2.5 flex items-center gap-2">
                  <CalendarDaysIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <p className="text-sm font-semibold text-green-800">{formatDate(selectedDate)}</p>
                </div>
              </div>

              {/* Time Slots */}
              <div className="animate-smooth-rise animate-delay-100">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                    <ClockIcon className="h-5 w-5 text-green-600" />
                    Available Slots
                  </h2>
                  {selectedSlots.length > 0 && (
                    <span className="text-xs font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">
                      {selectedSlots.length} selected
                    </span>
                  )}
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-5">
                  {loading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-4 border-green-200 border-t-green-600 mx-auto"></div>
                      <p className="text-gray-500 mt-4 text-sm">Loading availability...</p>
                    </div>
                  ) : (
                    <>
                      <TimeSlotGrid
                        availability={availability}
                        selectedSlots={selectedSlots}
                        bookingDate={selectedDate}
                        onSlotToggle={handleSlotToggle}
                        waitingListCounts={waitingListCounts}
                        isMemberUser={user && hasMembership}
                        animated={true}
                      />
                      {/* Slot Legend */}
                      <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap gap-x-4 gap-y-2">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <span className="w-3 h-3 rounded-sm bg-gradient-to-br from-green-50 to-green-100 border border-green-700"></span>
                          Available
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <span className="w-3 h-3 rounded-sm bg-gradient-to-br from-green-500 to-green-600 border border-green-600"></span>
                          Selected
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <span className="w-3 h-3 rounded-sm bg-gradient-to-br from-red-50 to-red-100 border border-red-600"></span>
                          Booked
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <span className="w-3 h-3 rounded-sm bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-500"></span>
                          Members Only
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Booking Summary */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden sticky top-16 animate-smooth-rise animate-delay-200">

                {/* Terms & Conditions Notice */}
                <div className="mx-4 mt-4 mb-3 flex items-start gap-3 rounded-xl border-2 border-red-400 bg-gradient-to-br from-red-50/80 to-white p-4 shadow-md ring-1 ring-red-200/50 animate-attention-blink">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-600">
                    <ShieldCheckIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800">
                      Read the policy carefully before booking.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowTermsReadOnly(true)}
                      className="mt-2 inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                    >
                      Terms & Conditions
                    </button>
                  </div>
                </div>

                {/* Court Info Banner */}
                {selectedCourt && (
                  <div className="bg-gradient-to-br from-green-600 via-green-700 to-green-800 px-5 py-5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-2">
                        <BuildingStorefrontIcon className="h-4 w-4 text-green-200" />
                        <span className="text-xs font-medium text-green-200 uppercase tracking-wider">Court</span>
                      </div>
                      <h3 className="text-lg font-bold text-white">{selectedCourt.name}</h3>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-white/90 text-sm font-semibold">₹{selectedCourt.hourly_rate}/hr</span>
                        <span className="text-green-200/60">•</span>
                        <span className="text-green-200 text-xs">Max 6 players</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-5 space-y-4">
                  {/* Date */}
                  <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                    <CalendarDaysIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <div>
                      <div className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Date</div>
                      <p className="font-semibold text-gray-900 text-sm">{formatDate(selectedDate)}</p>
                    </div>
                  </div>

                  {/* Selected Slots */}
                  {selectedSlots.length > 0 ? (
                    <div>
                      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2 px-1">
                        Selected Slots ({selectedSlots.length})
                      </div>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {selectedSlots.map(slot => (
                          <div key={slot.slot_time} className="flex justify-between items-center bg-green-50 border border-green-100 px-3 py-2 rounded-lg">
                            <span className="font-medium text-sm text-gray-800">{formatTime(slot.slot_time)}</span>
                            <span className="text-green-700 font-semibold text-sm">₹{selectedCourt?.hourly_rate}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                      <ClockIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-400 font-medium">No slots selected yet</p>
                      <p className="text-xs text-gray-300 mt-0.5">Tap a slot to add it</p>
                    </div>
                  )}

                  {/* Total & Book */}
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex justify-between items-baseline mb-4">
                      <span className="font-bold text-gray-900">Total</span>
                      <span className="text-2xl font-bold text-green-700">₹{totalAmount.toLocaleString()}</span>
                    </div>

                    <button
                      onClick={handleBooking}
                      disabled={loading || (user && selectedSlots.length === 0)}
                      className="w-full bg-green-600 text-white py-3.5 rounded-xl text-base font-semibold hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
                          Booking...
                        </div>
                      ) : !user ? (
                        'Sign in to Book'
                      ) : (
                        `Book ${selectedSlots.length} Slot${selectedSlots.length !== 1 ? 's' : ''}`
                      )}
                    </button>

                    {user && selectedSlots.length === 0 && (
                      <p className="text-xs text-gray-400 mt-3 text-center">
                        Select at least one time slot to continue
                      </p>
                    )}
                    {!user && (
                      <p className="text-xs text-gray-400 mt-3 text-center">
                        Sign in to start booking court
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>

      {/* Read-only terms pop-up (same as footer) */}
      <TermsModal
        isOpen={showTermsReadOnly}
        onClose={() => setShowTermsReadOnly(false)}
      />

      <TermsModal
        isOpen={showTermsModal}
        onClose={() => {
          setShowTermsModal(false);
          setTermsAccepted(false);
        }}
        showAccept={true}
        termsAccepted={termsAccepted}
        onAcceptChange={setTermsAccepted}
        onConfirm={handleBookingConfirm}
        loading={loading}
      />

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={async (authUser) => {
          setUser(authUser);
          await fetchUserProfile(authUser.email);
          setShowAuthModal(false);
        }}
      />

      <Footer />
      <WhatsAppButton />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}

export default GreenBookingApp;
